import { Countdown, Duration } from '@/components';
import PortalTooltip from '@/components/PortalTooltip';
import { Label } from '@/components/ui/label';
import { ProcessJob, ProcessJobType } from '@/graphql/graphql';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/utils';
import { CheckCircleSolid, ClockCircleSolid, DangerCircleSolid, InfoHexagonSolid } from '@mynaui/icons-react';
import { Loader2 } from 'lucide-react';
import { Fragment, FunctionComponent } from 'react';

interface iJobListProps {
  jobs: Partial<ProcessJob>[];
}

const jobTypeMap: Record<ProcessJobType, string> = {
  DWG: 'Processing DWG',
  DXF: 'Processing DXF',
  SHP: 'Processing SHP',
  E57: 'Processing E57',
  GEOJSON: 'Processing GeoJSON',
  GEOTIFF: 'Processing GeoTIFF',
  IMAGE: 'Processing Images',
  IMAGE_COMPRESSION: 'Compressing images',
  IPRJ: 'Processing Topodot config file',
  LAS: 'Processing point cloud',
  RCS: 'Processing RCS',
  PTS: 'Converting PTS to LAZ',
  IFC: 'Processing IFC',
  LANDXML: 'Processing LandXML',
  XGRIDS: 'Processing XGRIDS',
  FJD_IMAGE: 'Processing FJD images',
  POST: 'Post-processing',
};
const statusMap = {
  WAITING: {
    label: 'Waiting',
    className: 'bg-gray-500 text-white',
    icon: <ClockCircleSolid className='size-5 text-amber-500' />,
  },
  PROCESSING: {
    label: 'Processing',
    className: 'bg-blue-500 text-white',
    icon: <Loader2 className='size-5 animate-spin' />,
  },
  PROCESSED: {
    label: 'Processed',
    className: 'bg-green-500 text-white',
    icon: <CheckCircleSolid className='size-5 text-emerald-500' />,
  },
  FAILED: {
    label: 'Failed',
    className: 'bg-red-500 text-white',
    icon: <DangerCircleSolid className='size-5 text-rose-500' />,
  },
};

const JobList: FunctionComponent<iJobListProps> = ({ jobs }) => {
  const topLevelJobs = jobs?.filter((job) => !job.parentItemId);

  if (jobs?.length === 0) return null;

  return (
    <>
      <Label className='mt-8'>Jobs</Label>
      <div className='mt-2 border border-slate-200/70 rounded-md px-2 py-2 bg-white dark:bg-stone-800/70 dark:border-stone-800/70 gap-2 flex flex-col'>
        {topLevelJobs?.map((job) => {
          return <JobItem key={job.id} job={job} jobs={jobs ?? []} />;
        })}
      </div>
    </>
  );
};

const JobItem: FunctionComponent<{ job: Partial<ProcessJob>; jobs: Partial<ProcessJob>[] }> = ({ job, jobs }) => {
  const { me } = useMe();
  const childrenJobs = jobs?.filter((j) => j.parentItemId === job.id);
  return (
    <Fragment key={job.id}>
      <div key={job.id}>
        <div className='flex items-center gap-1.5'>
          {/* <ChevronRight className='size-4 stroke-2 text-slate-500' /> */}
          <PortalTooltip id={`job-status-${job.id}`}>{statusMap[job.status || 'WAITING'].label}</PortalTooltip>
          <div className='w-5 flex items-center justify-center [&>svg]:size-5' data-tooltip-id={`job-status-${job.id}`}>
            {statusMap[job.status || 'WAITING'].icon}
          </div>
          <span className='text-left font-medium text-sm text-slate-900 dark:text-white'>
            {jobTypeMap[job.type || 'DWG'] || job.type}

            {job.label ? <span className='opacity-60'>: {job.label}</span> : '...'}
          </span>
          <div className='text-xs text-slate-500 ml-auto dark:text-stone-400 flex items-center gap-2'>
            {me?.email.endsWith('@actegon.com') && job.logStream && (
              <InfoHexagonSolid
                className={cn(
                  'size-5 text-gray-500 fill-gray-100 dark:fill-stone-700 dark:text-stone-300 cursor-pointer',
                )}
                onClick={() => window.open(job.logStream!, '_blank')}
              />
            )}
            {job.estimatedCompletion && !job.completedAt ? (
              <Countdown estimatedCompletion={job.estimatedCompletion} startedAt={job.startedAt} />
            ) : job.startedAt ? (
              <Duration startedAt={job.startedAt} completedAt={job.completedAt} />
            ) : null}
          </div>
        </div>
      </div>
      {childrenJobs.length > 0 && (
        <div className='pl-6 flex flex-col gap-2'>
          {childrenJobs?.map((childJob) => (
            <JobItem key={childJob.id} job={childJob} jobs={jobs ?? []} />
          ))}
        </div>
      )}
    </Fragment>
  );
};

export default JobList;
